import { useCallback, useEffect, useRef, useState } from 'react';
import { redirectToCheckout } from './checkoutRedirect';
import { startSignupCheckout, type ApiFailure } from './signupApi';
import type { SignupCheckoutSession } from './signupSession';

export interface CheckoutLauncher {
  /** Asks the API for the payment page and sends the browser there. */
  launch: () => Promise<void>;
  /** True from the click until the failure arrives; stays true once the browser is leaving. */
  pending: boolean;
  /** Last failure code, cleared by the next attempt. */
  errorCode: string | null;
}

/**
 * One guarded "go and pay" action, shared by the verification page and the
 * progress page. A ref blocks a second click in the same tick (state alone
 * would let a fast double-click through before the re-render), and the button
 * stays disabled once the redirect has started.
 */
export function useCheckoutLauncher(
  session: SignupCheckoutSession | null,
  onFailure?: (failure: ApiFailure) => void,
): CheckoutLauncher {
  const [pending, setPending] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const onFailureRef = useRef(onFailure);
  onFailureRef.current = onFailure;

  useEffect(() => {
    mounted.current = true;
    // Coming back through the browser's back-forward cache revives the page
    // with the old "redirecting" state; the user must be able to try again.
    const onPageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) return;
      inFlight.current = false;
      setPending(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      mounted.current = false;
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  const launch = useCallback(async () => {
    if (!session || inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setErrorCode(null);

    const result = await startSignupCheckout(session);
    if (!mounted.current) return;

    if (result.ok) {
      if (redirectToCheckout(result.data.checkoutUrl)) return;
      inFlight.current = false;
      setPending(false);
      setErrorCode('checkout_invalid_url');
      return;
    }

    inFlight.current = false;
    setPending(false);
    setErrorCode(result.code);
    onFailureRef.current?.(result);
  }, [session]);

  return { launch, pending, errorCode };
}
